import { getCachedFeatureFlagVariant } from '@app/services/featureFlag';
import { LimitConfig } from '@screens/ChooseLimit/types';

/**
 * Shared analytics property helpers for the credit-limit / loan-offer funnel.
 *
 * `experiment_cohort` is the raw `multi-offer` variant name exactly as the
 * feature-flag API returns it (no A/B/C re-labelling). `recommended_offer` /
 * `selected_offer` are derived from the API payload's `offers[]` /
 * `selectedOfferCode`.
 */
const MULTI_OFFER_FLAG = 'multi-offer';

export const getMultiOfferVariant = (): string =>
  getCachedFeatureFlagVariant(MULTI_OFFER_FLAG, 'default');

/**
 * "Experiment cohort" property. Emits the raw `multi-offer` variant name exactly
 * as the feature-flag API returns it (e.g. `VARIANT_A` / `VARIANT_B`), falling
 * back to `'default'` when the API/cache has no value.
 */
export const getExperimentCohort = (): string => getMultiOfferVariant();

/**
 * "Recommended offer / Selected offer" property.
 *
 * Multi-offer users (the pledge/loan payload returns >= 2 `offers[]`, or the
 * selected offer is one of the explicit multi-offer codes) are on a specific
 * offer — surface its `offerCode` (e.g. `OFFER_50` / `OFFER_75`). Single-offer
 * users always map to `'default'`.
 */
const MULTI_OFFER_CODES = ['OFFER_50', 'OFFER_75'];

export const getRecommendedOffer = (
  selectedOfferCode?: string | null,
  offers?: { offerCode?: string }[] | null,
): string => {
  const isMultiOffer =
    (offers?.length ?? 0) >= 2 ||
    (!!selectedOfferCode && MULTI_OFFER_CODES.includes(selectedOfferCode));
  return isMultiOffer && selectedOfferCode ? selectedOfferCode : 'default';
};

/**
 * Builds the `offers` trait sent to the `multi-offer` feature-flag API from the
 * pledge/details `offers[]` payload. Joins every `offerCode` with `_` (so two
 * offers become `OFFER_50_OFFER_75`); a single offer yields just its code, and
 * an empty/absent array yields `''`.
 */
export const getOffersTrait = (
  offers?: { offerCode?: string | null }[] | null,
): string =>
  (offers ?? [])
    .map((offer) => offer?.offerCode)
    .filter((code): code is string => !!code)
    .join('_');

/**
 * "Interest rate: {ROI1, ROI2}" property. ROI1 is the lower-tier rate, ROI2 the
 * higher-tier rate (equal when the offer set is single-tier).
 */
export const getInterestRateProps = (limitConfig?: LimitConfig) => ({
  roi_1: limitConfig?.lowRate ?? 0,
  roi_2: limitConfig?.highRate ?? 0,
});

/**
 * Maps a route's RTA value to the analytics RTA label (CAMS / Kfin / MFC).
 */
export const getRtaLabel = (rta?: string): string => {
  switch (rta) {
    case 'CAMS':
      return 'CAMS';
    case 'KARVY':
      return 'Kfin';
    case 'MFCENTRAL':
      return 'MFC';
    default:
      return rta ?? '';
  }
};
